import { STATUS_TYPES } from './helpers';

/**
 * Determines if a month is in the past or is the current month
 * @param {number} year - The year of the month
 * @param {number} monthIndex - The month index (0-11)
 * @param {number} currentYear - The current year
 * @param {number} currentMonth - The current month index
 * @returns {Object} Object with isPastMonth and isCurrentMonth flags
 */
export function getMonthStatus(year, monthIndex, currentYear, currentMonth) {
  const isPastMonth =
    year < currentYear || (year === currentYear && monthIndex < currentMonth);
  const isCurrentMonth = monthIndex === currentMonth && year === currentYear;

  return { isPastMonth, isCurrentMonth };
}

/**
 * Filters campaigns by selected status types
 * Note: "completed" status is treated as "active" (live)
 * @param {Array} campaigns - Array of campaign objects
 * @param {Array} statusFilter - Array of status types to include
 * @returns {Array} Filtered campaigns
 */
export function getFilteredCampaigns(campaigns, statusFilter) {
  // When no filters selected, show all
  if (!statusFilter || statusFilter.length === 0) return campaigns;

  return campaigns.filter(campaign => {
    // Derive effective status for filtering
    let effectiveStatus = campaign.status;
    if (campaign.status === 'draft') {
      if (campaign.hasLivePost) effectiveStatus = STATUS_TYPES.ACTIVE;
      else if (campaign.hasScheduledPost)
        effectiveStatus = STATUS_TYPES.SCHEDULED;
    } else if (campaign.status === 'completed') {
      effectiveStatus = STATUS_TYPES.ACTIVE;
    }
    return statusFilter.includes(effectiveStatus);
  });
}

/**
 * Builds props object for a MonthCard component
 * @param {string} month - Month name
 * @param {number} year - Year
 * @param {number} monthIndex - Month index (0-11)
 * @param {number} index - Index in months array
 * @param {Object} campaignsData - Object mapping month names to campaign arrays
 * @param {Array} statusFilter - Array of highlight status types to include
 * @param {number} currentYear - Current year
 * @param {number} currentMonth - Current month index
 * @param {number} totalMonths - Total number of months being displayed
 * @param {Array} broadcastFilter - Array of broadcast status types (used to determine if campaigns should be hidden)
 * @returns {Object} Props for MonthCard
 */
export function getMonthCardProps(
  month,
  year,
  monthIndex,
  index,
  campaignsData,
  statusFilter,
  currentYear,
  currentMonth,
  totalMonths,
  broadcastFilter = [],
) {
  const { isPastMonth, isCurrentMonth } = getMonthStatus(
    year,
    monthIndex,
    currentYear,
    currentMonth,
  );

  const monthCampaigns = campaignsData[month] || [];
  // If broadcast filters selected but no highlight filters, hide campaigns
  const filteredCampaigns =
    broadcastFilter.length > 0 && statusFilter.length === 0
      ? []
      : getFilteredCampaigns(monthCampaigns, statusFilter);
  const zIndex = totalMonths - index;

  return {
    isCurrentMonth,
    filteredCampaigns,
    isPastMonth,
    zIndex,
  };
}

/**
 * Checks if a click event is on a modal or dropdown element
 * @param {Event} event - Click event
 * @param {boolean} isArchiveModalOpen - Whether archive modal is open
 * @param {boolean} isModalOpen - Whether date edit modal is open
 * @returns {boolean} True if click is on modal/dropdown
 */
export function isModalOrDropdownClick(event, isArchiveModalOpen, isModalOpen) {
  const modalSelectors = [
    '.MuiDialog-root',
    '.MuiBackdrop-root',
    '[role="dialog"]',
    '[data-cy="dropDown"]',
    '.dropdown-container',
    '.dropdown-menu',
  ];

  return (
    modalSelectors.some(selector => event.target.closest(selector)) ||
    isArchiveModalOpen ||
    isModalOpen
  );
}

/**
 * Gets tooltip text for date button based on campaign status
 * Note: "completed" status is treated as "active" (live)
 * @param {string} status - Campaign status
 * @param {Function} t - Translation function
 * @returns {string} Tooltip text
 */
export function getDateTooltipText(status, t) {
  if (status === STATUS_TYPES.ACTIVE || status === 'completed') {
    return t('calendar.activeCampaignsCannotBeEdited');
  }
  if (status === STATUS_TYPES.ARCHIVED) {
    return t('calendar.archivedCampaignsCannotBeEdited');
  }
  return t('calendar.editDate');
}

/**
 * Checks if date editing is disabled for a campaign status
 * Note: "completed" status is treated as "active" (live)
 * @param {string} status - Campaign status
 * @returns {boolean} True if date editing is disabled
 */
export function isDateEditDisabled(status) {
  return (
    status === STATUS_TYPES.ACTIVE ||
    status === STATUS_TYPES.ARCHIVED ||
    status === 'completed'
  );
}
